import React, {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {
    Table,
    Button,
    Card,
    Typography,
    Modal,
    Form,
    Input,
    DatePicker,
    message,
    Space,
    Select,
    Row,
    Col,
    Divider
} from "antd";
import api from "../../api";
import {
    CalendarOutlined,
    PlusOutlined,
    SearchOutlined
} from "@ant-design/icons";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {exportToExcel} from "../../utils/exportToExcel";

dayjs.extend(utc);
dayjs.extend(timezone);

const {Title} = Typography;
const {RangePicker} = DatePicker;

const RegistrationsPage: React.FC = () => {
    const [registrations, setRegistrations] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
    const [todayFilterActive, setTodayFilterActive] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [total, setTotal] = useState(0)
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
    });
    const [form] = Form.useForm();
    const navigate = useNavigate();

    useEffect(() => {
        fetchRegistrations(pagination.current, pagination.pageSize);
    }, [pagination.current, pagination.pageSize, dateRange, searchText]);

    const fetchRegistrations = async (page = 1, pageSize = 20) => {
        setLoading(true);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };

            if (dateRange?.[0] && dateRange?.[1]) {
                params.created_after = dateRange[0].startOf("day").toISOString();
                params.created_before = dateRange[1].endOf("day").toISOString();
            }

            if (searchText) {
                params.search = searchText;
            }

            const res = await api.get("/cc/registrations/", {params});

            setRegistrations(res.data.results);
            setTotal(res.data.count);
        } catch (err) {
            console.error("Failed to load registrations", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = async (values: any) => {
        try {
            const payload = {
                ...values,
                dob: values.dob.format("YYYY-MM-DD"),
                passport_issue: values.passport_issue.format("YYYY-MM-DD"),
                passport_expiration: values.passport_expiration.format("YYYY-MM-DD"),
            };
            await api.post("/cc/registrations/", payload);
            message.success("Registration created successfully");
            setIsModalVisible(false);
            form.resetFields();
            fetchRegistrations();
        } catch (err) {
            message.error("Failed to create registration");
        }
    };

    const disableUnder19 = (current: dayjs.Dayjs) => {
        return current && current > dayjs().subtract(19, 'year').endOf('day');
    };

    const disablePastDates = (current: dayjs.Dayjs) => {
        return current && current < dayjs().startOf("day");
    };

    return (
        <Card
            className="rounded-2xl shadow-sm"
            title={
                <Row justify="space-between" align="middle">
                    <Col>
                        <Title level={3} style={{margin: 0}}>
                            Casino Club Registrations
                        </Title>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={() => setIsModalVisible(true)}
                            className="rounded-md"
                        >
                            New Registration
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Space style={{marginBottom: 16}} wrap>
                <RangePicker
                    style={{minWidth: 250}}
                    onChange={(dates) => {
                        setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                        setTodayFilterActive(false);
                    }}
                    allowClear
                />
                <Button
                    icon={<CalendarOutlined/>}
                    type={todayFilterActive ? "default" : "primary"}
                    onClick={() => {
                        if (!todayFilterActive) {
                            const today = dayjs();
                            setDateRange([today, today]);
                        } else {
                            setDateRange(null);
                        }
                        setTodayFilterActive(!todayFilterActive);
                    }}
                    className="rounded-md"
                >
                    {todayFilterActive ? "Clear Today Filter" : "Today"}
                </Button>
                <Col>
                    <Input
                        placeholder="Search by name or passport number"
                        prefix={<SearchOutlined/>}
                        allowClear
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        style={{minWidth: 250}}
                    />
                </Col>
                <Button
                    type="primary"
                    onClick={() => {
                        const filtered = registrations
                            .filter((r) => !r.mmid)
                            .map((r) => ({
                                "First Name": r.first_name,
                                "Last Name": r.last_name,
                                "DOB": r.dob,
                                "Nationality": r.nationality,
                                "Address": r.address,
                                "Phone": r.phone,
                                "Passport Number": r.passport_number,
                                "Passport Issue": r.passport_issue,
                                "Passport Expiration": r.passport_expiration,
                                "MMID": r.mmid || "Pending",
                            }));

                        exportToExcel(filtered, `Unregistered_Users_${dayjs().format("YYYY-MM-DD")}.xlsx`);
                    }}
                >
                    Export Unregistered to Excel
                </Button>
            </Space>

            <Table
                rowKey="id"
                dataSource={registrations}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: total,
                    showSizeChanger: true,
                    onChange: (page, pageSize) => {
                        setPagination({current: page, pageSize});
                    },
                }}
                scroll={{x: true}}
                columns={[
                    {title: "First Name", dataIndex: "first_name"},
                    {title: "Last Name", dataIndex: "last_name"},
                    {title: "DOB", dataIndex: "dob"},
                    {title: "Nationality", dataIndex: "nationality"},
                    {title: "Phone", dataIndex: "phone"},
                    {title: "MMID", dataIndex: "mmid"},
                    {
                        title: "Created At",
                        dataIndex: "created_at",
                        render: (val: string) =>
                            val
                                ? dayjs(val).tz("Asia/Seoul").format("YYYY-MM-DD HH:mm") + " KST"
                                : "-",
                    },
                ]}
                onRow={(record) => ({
                    onClick: () => navigate(`/cc/registrations/${record.id}`),
                    style: {
                        backgroundColor: record.mmid ? "#2f2f2f" : "#ffffff",
                        color: record.mmid ? "white" : undefined,
                        cursor: "pointer",
                        transition: "background-color 0.3s ease",
                    },
                })}
                className="rounded-xl overflow-hidden"
            />

            <Modal
                title={<span className="text-base font-semibold">New Registration</span>}
                open={isModalVisible}
                onCancel={() => setIsModalVisible(false)}
                onOk={() => form.submit()}
                okText="Save"
                width={700}
                centered
                okButtonProps={{className: "rounded-md"}}
                cancelButtonProps={{className: "rounded-md"}}
            >
                <Divider className="my-2"/>
                <Form layout="vertical" form={form} onFinish={handleCreate}>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="first_name"
                                label="First Name"
                                rules={[{required: true, message: "Please enter first name"}]}
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="last_name"
                                label="Last Name"
                                rules={[{required: true, message: "Please enter last name"}]}
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="email" label="Email">
                                <Input placeholder="you@example.com"/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="dob"
                                label="Date of Birth"
                                rules={[{required: true, message: "Please select a birth date"}]}
                            >
                                <DatePicker
                                    style={{width: "100%"}}
                                    disabledDate={disableUnder19}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="nationality"
                                label="Nationality"
                                rules={[{required: true, message: "Please enter nationality"}]}
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="phone"
                                label="Phone"
                                rules={[{required: true, message: "Please enter phone number"}]}
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item
                                name="address"
                                label="Address"
                                rules={[{required: true, message: "Please enter address"}]}
                            >
                                <Input.TextArea rows={2}/>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="passport_number"
                                label="Passport Number"
                                rules={[
                                    {required: true, message: "Passport number is required"},
                                    {
                                        pattern: /^[A-Z0-9]{5,20}$/i,
                                        message: "Enter a valid passport number",
                                    },
                                ]}
                            >
                                <Input/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="passport_issue"
                                label="Passport Issue"
                                rules={[{required: true, message: "Please select issue date"}]}
                            >
                                <DatePicker
                                    style={{width: "100%"}}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="passport_expiration"
                                label="Passport Expiration"
                                rules={[{required: true, message: "Please select expiration date"}]}
                            >
                                <DatePicker
                                    style={{width: "100%"}}
                                    disabledDate={disablePastDates}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="mmid" label="MMID">
                                <Input placeholder="(Update When Received)"/>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="agent" label="Agent">
                                <Input placeholder="Agent name, if applicable"/>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="source" label="Source">
                                <Select placeholder="Select source" allowClear>
                                    <Select.Option value="Official LINE">Official LINE</Select.Option>
                                    <Select.Option value="Private LINE">Private LINE</Select.Option>
                                    <Select.Option value="Agent LINE">Agent LINE</Select.Option>
                                    <Select.Option value="Telegram">Telegram</Select.Option>
                                    <Select.Option value="Email">Email</Select.Option>
                                    <Select.Option value="Wallet">Wallet</Select.Option>
                                    <Select.Option value="Poker">Poker</Select.Option>
                                    <Select.Option value="Walk-in">Walk-in</Select.Option>
                                    <Select.Option value="Event">Event</Select.Option>
                                    <Select.Option value="Other">Other</Select.Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </Card>
    );
};

export default RegistrationsPage;
